import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../services/api';
import { US_TIMEZONES } from '../services/timezone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UserPlus, Plus, MagnifyingGlass, Phone, MapPin, FirstAid, Warning, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  user_id: 'none', name: '', date_of_birth: '', address: '', email: '',
  emergency_contact: '', emergency_phone: '', emergency_email: '',
  medical_conditions: '', allergies: '', notes: '',
  timezone: 'America/New_York', start_date: ''
};

export const LeadsTab = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [converting, setConverting] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await clientsAPI.getAll('lead'); setLeads(res.data); }
    catch (e) { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        medical_conditions: formData.medical_conditions.split(',').map(s => s.trim()).filter(Boolean),
        allergies: formData.allergies.split(',').map(s => s.trim()).filter(Boolean)
      };
      await clientsAPI.create(data, true);
      toast.success('Lead created');
      setIsCreateOpen(false);
      setFormData(EMPTY_FORM);
      load();
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleConvert = async (lead, e) => {
    e.stopPropagation();
    setConverting(lead.id);
    try {
      await clientsAPI.convertToActive(lead.id);
      toast.success(`${lead.name} is now an active client`);
      load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to convert lead'); }
    finally { setConverting(null); }
  };

  const filtered = leads.filter(l =>
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="leads-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input placeholder="Search leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-xl" data-testid="lead-search-input" />
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl gap-2" data-testid="add-lead-btn">
              <Plus weight="bold" className="w-4 h-4" /> Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-create-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Lead Profile</DialogTitle>
              <DialogDescription>Same profile as an active client — stays in Leads until converted</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateLead} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" required className="rounded-xl" data-testid="lead-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="rounded-xl" data-testid="lead-dob-input" />
                </div>
                <div className="space-y-2">
                  <Label>Expected Start Date</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="rounded-xl" data-testid="lead-start-date-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main St, City, State 12345" className="rounded-xl" data-testid="lead-address-input" />
              </div>
              <div className="space-y-2">
                <Label>Time Zone</Label>
                <Select value={formData.timezone} onValueChange={(v) => setFormData({ ...formData, timezone: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="lead-timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {US_TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="client@example.com" className="rounded-xl" data-testid="lead-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Emergency Contact</Label>
                  <Input value={formData.emergency_contact} onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })} placeholder="Jane Doe (Daughter)" className="rounded-xl" data-testid="lead-emergency-contact-input" />
                </div>
                <div className="space-y-2">
                  <Label>Emergency Phone</Label>
                  <Input value={formData.emergency_phone} onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })} placeholder="+1 555-0000" className="rounded-xl" data-testid="lead-emergency-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact Email</Label>
                <Input type="email" value={formData.emergency_email} onChange={(e) => setFormData({ ...formData, emergency_email: e.target.value })} placeholder="family-member@example.com" className="rounded-xl" data-testid="lead-emergency-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Medical Conditions (comma-separated)</Label>
                <Textarea value={formData.medical_conditions} onChange={(e) => setFormData({ ...formData, medical_conditions: e.target.value })} placeholder="Diabetes, Hypertension" className="rounded-xl" data-testid="lead-conditions-input" />
              </div>
              <div className="space-y-2">
                <Label>Allergies (comma-separated)</Label>
                <Input value={formData.allergies} onChange={(e) => setFormData({ ...formData, allergies: e.target.value })} placeholder="Penicillin, Shellfish" className="rounded-xl" data-testid="lead-allergies-input" />
              </div>
              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Referral source, needs, next steps..." className="rounded-xl" data-testid="lead-notes-input" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 rounded-xl" data-testid="create-lead-submit">Create Lead</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserPlus weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No leads yet</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchTerm ? 'Try a different search term' : 'Add a potential client to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((lead) => (
            <Card
              key={lead.id}
              className="rounded-2xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate(`/clients/${lead.id}`)}
              data-testid={`lead-card-${lead.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-violet-100">
                      <UserPlus weight="duotone" className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-lg">{lead.name}</CardTitle>
                      {lead.date_of_birth && <CardDescription>DOB: {lead.date_of_birth}</CardDescription>}
                    </div>
                  </div>
                  <Badge className="rounded-full text-xs bg-violet-100 text-violet-800">Lead</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {lead.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">{lead.address}</span>
                  </div>
                )}
                {lead.emergency_phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone weight="duotone" className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{lead.emergency_contact}: {lead.emergency_phone}</span>
                  </div>
                )}
                {lead.medical_conditions?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <FirstAid weight="duotone" className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex flex-wrap gap-1">
                      {lead.medical_conditions.map((c, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs rounded-full">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {lead.allergies?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Warning weight="duotone" className="w-4 h-4 text-destructive mt-1" />
                    <div className="flex flex-wrap gap-1">
                      {lead.allergies.map((a, idx) => (
                        <Badge key={idx} className="text-xs rounded-full bg-destructive/10 text-destructive">{a}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="pt-2">
                  <Button
                    variant="outline" size="sm"
                    className="rounded-xl gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    onClick={(e) => handleConvert(lead, e)}
                    disabled={converting === lead.id}
                    data-testid={`convert-lead-btn-${lead.id}`}
                  >
                    <CheckCircle weight="bold" className="w-3 h-3" />
                    {converting === lead.id ? 'Converting...' : 'Convert to Active'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
